import React, { useMemo, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useToast } from '../../../hooks/useToast';
import {
  AdminPlan,
  AdminPlanFeature,
  FeatureValue,
  PlanSection,
  UNLIMITED,
  describeValue,
  freePlanOf,
  timesFree,
  plansApi,
} from '../../../api/plans';
import { FeatureEditor } from './FeatureEditor';

/**
 * The comparison matrix, editable in place.
 *
 * Rows are capabilities grouped the way the app renders them; columns are
 * plans. A cell is clicked, changed, and saved on its own — there is no
 * "save all", because a half-applied bulk edit to pricing is worse than a
 * slower one that is always consistent.
 */

/** The editor for one cell, chosen by what the feature says it accepts. */
const CellEditor: React.FC<{
  feature: AdminPlanFeature;
  value: FeatureValue | undefined;
  onCommit: (value: FeatureValue) => void;
  onCancel: () => void;
}> = ({ feature, value, onCommit, onCancel }) => {
  const [num, setNum] = useState(typeof value === 'number' ? String(value) : '');
  // The text editor's own draft. It used to show the saved value and ignore
  // what was typed, then save an empty string.
  const [text, setText] = useState(typeof value === 'string' && value !== UNLIMITED ? value : '');

  if (feature.valueType === 'boolean') {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button size="sm" variant={value === true ? 'primary' : 'secondary'} onClick={() => onCommit(true)}>
          On
        </Button>
        <Button size="sm" variant={value === false ? 'primary' : 'secondary'} onClick={() => onCommit(false)}>
          Off
        </Button>
      </div>
    );
  }

  if (feature.valueType === 'enum') {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {(feature.enumOptions ?? []).map((opt) => (
          <Button
            key={opt}
            size="sm"
            variant={value === opt ? 'primary' : 'secondary'}
            onClick={() => onCommit(opt)}
          >
            {opt}
          </Button>
        ))}
        <Button size="sm" variant={value === false ? 'primary' : 'ghost'} onClick={() => onCommit(false)}>
          Off
        </Button>
      </div>
    );
  }

  if (feature.valueType === 'number') {
    const validNum = /^\d+$/.test(num.trim());
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            autoFocus
            value={num}
            inputMode="numeric"
            onChange={(e) => setNum(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && validNum) onCommit(Number(num));
              if (e.key === 'Escape') onCancel();
            }}
            placeholder={feature.unit ?? 'number'}
            style={{
              width: 78,
              padding: '5px 8px',
              fontSize: 13,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--neutral-300)',
              background: 'var(--neutral-100)',
              color: 'var(--neutral-800)',
            }}
          />
          <Button
            size="sm"
            disabled={!validNum}
            onClick={() => validNum && onCommit(Number(num))}
          >
            Set
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <Button
            size="sm"
            variant={value === UNLIMITED ? 'primary' : 'secondary'}
            onClick={() => onCommit(UNLIMITED)}
          >
            Unlimited
          </Button>
          <Button size="sm" variant={value === false ? 'primary' : 'ghost'} onClick={() => onCommit(false)}>
            Off
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && text.trim()) onCommit(text.trim());
          if (e.key === 'Escape') onCancel();
        }}
        style={{
          padding: '5px 8px',
          fontSize: 13,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--neutral-300)',
          background: 'var(--neutral-100)',
          color: 'var(--neutral-800)',
        }}
      />
      <Button size="sm" disabled={!text.trim()} onClick={() => onCommit(text.trim())}>
        Set
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onCommit(false)}>
        Off
      </Button>
    </div>
  );
};

/** What every business gets of a feature once it is removed, in words. */
function everybodyGets(feature: AdminPlanFeature): string {
  const value = feature.ungatedValue ?? true;
  if (value === UNLIMITED) return 'unlimited';
  if (value === true) return 'it';
  return `"${String(value)}"`;
}

type Pending =
  | { kind: 'removeFeature'; feature: AdminPlanFeature }
  | { kind: 'removeSection'; section: PlanSection; features: AdminPlanFeature[] };

const headerCell: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--neutral-600)',
  background: 'var(--neutral-200)',
  borderBottom: '1px solid var(--neutral-300)',
};

const linkButton: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  fontSize: 12,
  fontFamily: 'var(--font-family)',
  color: 'var(--blue-600)',
  cursor: 'pointer',
  textTransform: 'none',
  letterSpacing: 0,
  fontWeight: 500,
};

/**
 * The plan builder: sections of features down the side, plans across.
 *
 * Sections and features can be added, renamed, moved and removed here, and
 * whatever is here is what the business app's comparison screen shows.
 *
 * ## Removing is not switching off
 *
 * "Off" in a cell means that plan does not include the feature. Removing a
 * feature (or its whole section) takes it out of pricing altogether: it
 * disappears from the app's comparison and every business gets it, at its
 * most generous. Restoring puts the plans' own values back.
 */
export const FeatureMatrix: React.FC<{
  plans: AdminPlan[];
  features: AdminPlanFeature[];
  sections?: PlanSection[];
  matrix: Record<string, Record<string, FeatureValue>>;
  canManage: boolean;
  onChanged: () => void;
}> = ({ plans, features, sections: sectionRows, matrix, canManage, onChanged }) => {
  const toast = useToast();
  const [editing, setEditing] = useState<{ planId: string; featureKey: string } | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ feature: AdminPlanFeature | null; sectionId: string | null } | null>(null);

  // An older backend sends groups only: show them, without managing them.
  const managed = !!sectionRows && canManage;
  const sections: PlanSection[] = useMemo(() => {
    if (sectionRows) return [...sectionRows].sort((a, b) => a.sort - b.sort);
    const seen: PlanSection[] = [];
    for (const f of features) {
      if (!seen.some((x) => x.id === f.group)) {
        seen.push({ id: f.group, name: f.group, sort: seen.length, archived: false, featureCount: 0 });
      }
    }
    return seen;
  }, [sectionRows, features]);
  const active = sections.filter((x) => !x.archived);
  const removedSections = sections.filter((x) => x.archived);
  const sectionOf = (f: AdminPlanFeature) => (sectionRows ? f.sectionId ?? '' : f.group);
  const featuresIn = (sectionId: string, archived: boolean) =>
    features.filter((f) => sectionOf(f) === sectionId && f.archived === archived);
  const removedFeatures = features.filter(
    (f) => f.archived && !removedSections.some((x) => x.id === sectionOf(f)),
  );

  const commit = async (plan: AdminPlan, feature: AdminPlanFeature, value: FeatureValue) => {
    const cellId = `${plan.id}:${feature.key}`;
    try {
      setSaving(cellId);
      setError(null);
      await plansApi.setFeature(plan.id, feature.key, value);
      setEditing(null);
      onChanged();
    } catch (e: any) {
      // The server owns validation — it is the only place that knows what
      // this particular key accepts — so its message is the useful one. It
      // can be a list (one per failed rule), which rendered as run-together
      // text.
      const message = e?.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join(' ')
          : typeof message === 'string'
            ? message
            : 'Could not save that value.',
      );
    } finally {
      setSaving(null);
    }
  };

  async function run(action: () => Promise<unknown>, done: string, fallback: string) {
    setBusy(true);
    try {
      await action();
      toast.success(done);
      onChanged();
      return true;
    } catch (err) {
      toast.error(err, fallback);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addSection() {
    const name = adding?.trim();
    if (!name) return;
    if (await run(() => plansApi.createSection(name), `${name} added.`, 'Could not add that section.')) {
      setAdding(null);
    }
  }

  async function renameSection() {
    if (!renaming) return;
    const name = renaming.name.trim();
    const section = sections.find((x) => x.id === renaming.id);
    if (!name || name === section?.name) return setRenaming(null);
    if (
      await run(
        () => plansApi.updateSection(renaming.id, { name }),
        `Renamed to ${name}.`,
        'Could not rename that section.',
      )
    ) {
      setRenaming(null);
    }
  }

  /** Swap with the neighbour, writing tidy sort numbers for everything. */
  async function move(section: PlanSection, by: -1 | 1) {
    const order = [...active];
    const i = order.findIndex((x) => x.id === section.id);
    const j = i + by;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    await run(
      async () => {
        for (let k = 0; k < order.length; k++) {
          const sort = (k + 1) * 10;
          if (order[k].sort !== sort) await plansApi.updateSection(order[k].id, { sort });
        }
      },
      'Order saved.',
      'Could not move that section.',
    );
  }

  async function confirmRemove() {
    if (!pending) return;
    const ok =
      pending.kind === 'removeFeature'
        ? await run(
            () => plansApi.updateFeature(pending.feature.id, { archived: true }),
            `${pending.feature.label} removed. Every business has it now.`,
            'Could not remove that feature.',
          )
        : await run(
            () => plansApi.removeSection(pending.section.id),
            `${pending.section.name} removed. Every business has its features now.`,
            'Could not remove that section.',
          );
    if (ok) setPending(null);
  }

  const dialog = pending
    ? pending.kind === 'removeFeature'
      ? {
          title: `Remove ${pending.feature.label}?`,
          message: `It leaves every plan and the app's comparison, and every business gets ${everybodyGets(
            pending.feature,
          )}, whatever their plan. You can restore it from "Show removed", with each plan's values as they were.`,
        }
      : {
          title: `Remove the ${pending.section.name} section?`,
          message: `Its ${pending.features.length} ${
            pending.features.length === 1 ? 'feature leaves' : 'features leave'
          } every plan and the app's comparison, and every business gets them all, whatever their plan${
            pending.features.length
              ? `: ${pending.features.map((f) => f.label).join(', ')}`
              : ''
          }. Restoring the section brings them back as they were.`,
        }
    : null;

  const renderRow = (feature: AdminPlanFeature) => (
    <tr key={feature.id}>
      <td style={{ padding: '10px 16px', borderBottom: '1px solid var(--neutral-200)', verticalAlign: 'top' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-800)' }}>{feature.label}</div>
        {feature.hint && <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>{feature.hint}</div>}
        <div style={{ fontSize: 11, color: 'var(--neutral-500)', fontFamily: 'monospace' }}>{feature.key}</div>
        {managed && (
          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <button
              type="button"
              style={linkButton}
              onClick={() => setEditor({ feature, sectionId: feature.sectionId ?? null })}
            >
              Edit
            </button>
            <button
              type="button"
              style={{ ...linkButton, color: 'var(--danger-600)' }}
              onClick={() => setPending({ kind: 'removeFeature', feature })}
            >
              Remove
            </button>
          </div>
        )}
      </td>
      {plans.map((plan) => {
        const value = matrix[plan.key]?.[feature.key];
        const free = freePlanOf(plans);
        // What the apps will show, under the number the admin is setting.
        const relative = timesFree(value, free ? matrix[free.key]?.[feature.key] : undefined, feature);
        const isEditing = editing?.planId === plan.id && editing?.featureKey === feature.key;
        const cellId = `${plan.id}:${feature.key}`;
        return (
          <td
            key={plan.id}
            style={{ padding: '10px 12px', borderBottom: '1px solid var(--neutral-200)', verticalAlign: 'top' }}
          >
            {isEditing ? (
              <CellEditor
                feature={feature}
                value={value}
                onCommit={(v) => void commit(plan, feature, v)}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <button
                type="button"
                disabled={!canManage || saving === cellId}
                onClick={() => setEditing({ planId: plan.id, featureKey: feature.key })}
                style={{
                  background: 'transparent',
                  border: '1px solid transparent',
                  borderRadius: 'var(--radius-sm)',
                  padding: '4px 8px',
                  fontSize: 13,
                  fontFamily: 'var(--font-family)',
                  cursor: canManage ? 'pointer' : 'default',
                  color: value === false || value === undefined ? 'var(--neutral-500)' : 'var(--neutral-800)',
                }}
              >
                {saving === cellId ? 'Saving…' : describeValue(value, feature)}
                {relative && saving !== cellId ? (
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--neutral-500)' }}>
                    Shown as {relative}
                  </span>
                ) : null}
              </button>
            )}
          </td>
        );
      })}
    </tr>
  );

  return (
    <Card padding={0}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          padding: '14px 16px',
          borderBottom: '1px solid var(--neutral-300)',
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--neutral-700)', maxWidth: 560 }}>
          {canManage
            ? 'Click a cell to change what a plan includes. Removing a feature or section takes it out of pricing and gives it to every business.'
            : 'Read only — you need “Manage plans & pricing” to edit.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {managed && adding === null && (
            <Button size="sm" variant="secondary" onClick={() => setAdding('')}>
              Add section
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setShowRemoved((s) => !s)}>
            {showRemoved ? 'Hide removed' : 'Show removed'}
          </Button>
        </div>
      </div>

      {adding !== null && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void addSection();
          }}
          style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--neutral-300)' }}
        >
          <input
            autoFocus
            aria-label="New section name"
            placeholder="Section name, for example Support"
            value={adding}
            maxLength={60}
            onChange={(e) => setAdding(e.target.value)}
            style={{
              flex: 1,
              maxWidth: 320,
              height: 34,
              padding: '0 10px',
              fontSize: 13,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--neutral-300)',
              background: 'var(--neutral-100)',
              color: 'var(--neutral-800)',
            }}
          />
          <Button size="sm" type="submit" disabled={busy || !adding.trim()}>
            Add
          </Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => setAdding(null)}>
            Cancel
          </Button>
        </form>
      )}

      {error && <div style={{ padding: '10px 16px', color: 'var(--danger-900)', fontSize: 13 }}>{error}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 760 }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '10px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--neutral-700)',
                  borderBottom: '1px solid var(--neutral-300)',
                  minWidth: 260,
                }}
              >
                Feature
              </th>
              {plans.map((p) => (
                <th
                  key={p.id}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--neutral-700)',
                    borderBottom: '1px solid var(--neutral-300)',
                    minWidth: 150,
                  }}
                >
                  {p.name}
                  {p.status !== 'published' && (
                    <span style={{ color: 'var(--neutral-500)', fontWeight: 400 }}> · {p.status}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {active.map((section, index) => {
              const rows = featuresIn(section.id, false);
              const isRenaming = renaming?.id === section.id;
              return (
                <React.Fragment key={section.id}>
                  <tr>
                    <td colSpan={plans.length + 1} style={headerCell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        {isRenaming ? (
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              void renameSection();
                            }}
                            style={{ display: 'flex', gap: 6 }}
                          >
                            <input
                              autoFocus
                              aria-label="Section name"
                              value={renaming.name}
                              maxLength={60}
                              onChange={(e) => setRenaming({ id: section.id, name: e.target.value })}
                              onKeyDown={(e) => e.key === 'Escape' && setRenaming(null)}
                              style={{ height: 28, padding: '0 8px', fontSize: 13, textTransform: 'none' }}
                            />
                            <Button size="sm" type="submit" disabled={busy}>
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" type="button" onClick={() => setRenaming(null)}>
                              Cancel
                            </Button>
                          </form>
                        ) : (
                          <span>{section.name}</span>
                        )}
                        {managed && !isRenaming && (
                          <>
                            <button
                              type="button"
                              style={linkButton}
                              onClick={() => setRenaming({ id: section.id, name: section.name })}
                            >
                              Rename
                            </button>
                            <button
                              type="button"
                              style={linkButton}
                              onClick={() => setEditor({ feature: null, sectionId: section.id })}
                            >
                              Add feature
                            </button>
                            <button
                              type="button"
                              aria-label={`Move ${section.name} up`}
                              disabled={busy || index === 0}
                              style={{ ...linkButton, opacity: index === 0 ? 0.4 : 1 }}
                              onClick={() => void move(section, -1)}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              aria-label={`Move ${section.name} down`}
                              disabled={busy || index === active.length - 1}
                              style={{ ...linkButton, opacity: index === active.length - 1 ? 0.4 : 1 }}
                              onClick={() => void move(section, 1)}
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              style={{ ...linkButton, color: 'var(--danger-600)' }}
                              onClick={() => setPending({ kind: 'removeSection', section, features: rows })}
                            >
                              Remove section
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={plans.length + 1}
                        style={{ padding: '10px 16px', fontSize: 13, color: 'var(--neutral-500)', borderBottom: '1px solid var(--neutral-200)' }}
                      >
                        No features yet. The app hides a section until it has one.
                      </td>
                    </tr>
                  ) : (
                    rows.map(renderRow)
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showRemoved && (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--neutral-300)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)' }}>Removed</div>
          <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
            Every business has these, whatever their plan. Restoring one puts each plan&apos;s value back as it was.
          </div>
          {removedSections.length === 0 && removedFeatures.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>Nothing has been removed.</div>
          )}
          {removedSections.map((section) => {
            const inside = featuresIn(section.id, true);
            return (
              <div key={section.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{section.name} section</span>
                <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                  {inside.length ? inside.map((f) => f.label).join(', ') : 'no features'}
                </span>
                {managed && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () => plansApi.restoreSection(section.id),
                        `${section.name} restored.`,
                        'Could not restore that section.',
                      )
                    }
                  >
                    Restore
                  </Button>
                )}
              </div>
            );
          })}
          {removedFeatures.map((feature) => (
            <div key={feature.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{feature.label}</span>
              <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                {sections.find((x) => x.id === sectionOf(feature))?.name ?? feature.group} · everybody gets{' '}
                {everybodyGets(feature)}
              </span>
              {managed && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => plansApi.updateFeature(feature.id, { archived: false }),
                      `${feature.label} restored.`,
                      'Could not restore that feature.',
                    )
                  }
                >
                  Restore
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!dialog}
        title={dialog?.title ?? ''}
        message={dialog?.message ?? ''}
        confirmLabel="Remove"
        variant="danger"
        loading={busy}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setPending(null)}
      />

      <FeatureEditor
        open={!!editor}
        feature={editor?.feature ?? null}
        sections={active}
        defaultSectionId={editor?.sectionId ?? null}
        onClose={() => setEditor(null)}
        onSaved={onChanged}
      />
    </Card>
  );
};
